<script setup lang="ts">
import { useDynamicHeight } from '@/composables/useDynamicHeight';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { type IncidentRecord } from '@/api/incident-records';
import { clinicalAuthorName } from '@/utils/clinical-author';
import { useShowVoided } from '@/composables/useShowVoided';
import ClinicalRecordVoid from '@/components/clinical-records/clinical-record-void.vue';
import ClinicalVoidMark from '@/components/clinical-records/clinical-void-mark.vue';
import ShowVoidedSwitch from '@/components/clinical-records/show-voided-switch.vue';

const props = defineProps<{ incidentRecords: IncidentRecord[] }>();

// Anulados show on demand, crossed out.
const { showVoided, voidedCount, visibleItems } = useShowVoided(() => props.incidentRecords);

const { dynamicHeight } = useDynamicHeight();
const { tableProps } = useResponsiveTable();

const headers = [
  { title: 'Incidente', key: 'incidentDefinitionName', align: 'start' },
  { title: 'Descripción', key: 'incidentDefinitionDescription', align: 'start' },
  { title: 'Reportado el', key: 'humanReportedAt', align: 'center' },
  { title: 'Registrado por', key: 'author', align: 'start', sortable: false },
  { title: 'Observaciones', key: 'observations', align: 'start' },
] as const;
</script>

<template>
  <v-data-table
    v-bind="tableProps"
    :headers="headers"
    :header-props="{ class: 'font-weight-bold' }"
    :items="visibleItems"
    :height="dynamicHeight"
    fixed-header
    item-key="id"
    class="pa-2"
    loading-text="Cargando registros de incidentes..."
    no-data-text="No hay registros de incidentes en este turno"
  >
    <template
      v-if="voidedCount > 0"
      #top
    >
      <div class="justify-end d-flex pa-2">
        <show-voided-switch
          v-model="showVoided"
          :count="voidedCount"
        />
      </div>
    </template>
    <template #[`item.incidentDefinitionName`]="{ item }">
      <v-chip
        color="error"
        size="small"
        variant="tonal"
        :class="{ 'text-decoration-line-through': item.voided }"
      >
        {{ item.incidentDefinitionName }}
      </v-chip>
      <clinical-void-mark
        :voided="item.voided"
        :details="item.voidDetails"
        class="mt-1"
      />
    </template>
    <template #[`item.humanReportedAt`]="{ item }">
      <span class="text-no-wrap">{{ item.humanReportedAt }}</span>
      <clinical-record-void
        item-type="IncidentRecord"
        :item-id="item.id"
        :can-void="item.canVoid"
        :label="`Incidente ${item.incidentDefinitionName} · ${item.humanReportedAt}`"
      />
    </template>
    <template #[`item.author`]="{ item }">
      {{ clinicalAuthorName(item.author) || '-' }}
    </template>
    <template #[`item.observations`]="{ item }">
      <p
        class="text-wrap"
        :class="{ 'text-decoration-line-through text-medium-emphasis': item.voided }"
      >
        {{ item.observations || '-' }}
      </p>
    </template>
  </v-data-table>
</template>
